import React from "react";
import { useTheme } from "../context/ThemeContext";

const CONTENT = {
  privacy: {
    eyebrow: "YOUR DATA",
    title: "Privacy Policy",
    intro: "QuantSoc is built to make quantitative interview preparation more useful, focused, and respectful of your privacy.",
    sections: [
      ["Information we collect", "When you create an account, we may collect your name, email address, profile details, and activity such as problem ratings or Problem of the Day submissions. We also receive basic technical information needed to keep the service secure and reliable."],
      ["How we use information", "We use this information to provide accounts, personalize your experience, operate rankings, improve our problem archive, respond to messages, and prevent abuse. We do not sell your personal information."],
      ["Third-party services", "QuantSoc uses service providers for hosting, analytics, authentication, email or contact processing, and advertising. These providers may process limited information on our behalf under their own privacy policies."],
      ["Your choices", "You can contact us to ask about, correct, or delete personal information associated with your account. Some records may need to be retained when required for security, legal, or operational reasons."],
      ["Updates", "We may update this policy as the service changes. The latest version will always be published on this page."],
    ],
  },
  terms: {
    eyebrow: "THE AGREEMENT",
    title: "Terms of Use",
    intro: "By using QuantSoc, you agree to use the service responsibly and to these straightforward terms.",
    sections: [
      ["Educational use", "QuantSoc provides interview-preparation material for educational purposes. Content is curated in good faith, but it may contain errors and should not be treated as professional, academic, or financial advice."],
      ["Your account", "Keep your account information accurate and your credentials secure. You are responsible for activity under your account and must not impersonate another person or attempt to gain unauthorized access."],
      ["Content and conduct", "Do not scrape, redistribute, abuse, disrupt, or reverse engineer the service. Do not submit unlawful, misleading, or harmful material. We may remove content or restrict access when necessary to protect the community."],
      ["Intellectual property", "QuantSoc and its original content, branding, and software belong to QuantSoc or its contributors. You may use the material for personal learning, but broader reuse requires permission unless otherwise stated."],
      ["Availability", "We work to keep QuantSoc useful and available, but the service may change or be temporarily unavailable. We provide it on an as-is basis to the extent permitted by law."],
    ],
  },
  about: {
    eyebrow: "THE PROJECT",
    title: "About QuantSoc",
    intro: "A considered archive of quantitative finance, probability, statistics, and mathematics problems for people preparing to think clearly under pressure.",
    sections: [
      ["What we make", "QuantSoc brings interview-style problems, worked solutions, difficulty signals, firm tags, and community feedback into one focused study space."],
      ["Why it exists", "Strong preparation is less about memorizing tricks and more about seeing the structure of a problem. We organize the material so you can practice that kind of thinking repeatedly."],
      ["Built by Prism Labs", "QuantSoc is an initiative by Prism Labs, a small team interested in thoughtful software, rigorous problem solving, and tools that help ambitious learners do better work."],
      ["Help shape the archive", "Found an error, have a problem suggestion, or want to collaborate? Visit our contact page and send us a note. Every useful correction makes the archive stronger."],
    ],
  },
};

export default function InfoPage({ page, nav }) {
  const { t } = useTheme();
  const content = CONTENT[page];

  return (
    <article style={{ maxWidth: 780, margin: "0 auto", padding: "104px 32px 96px" }}>
      <span style={{ fontSize: 11, color: t.text3, letterSpacing: "0.12em", fontWeight: 700 }}>{content.eyebrow}</span>
      <h1 style={{ fontSize: 42, color: t.text, margin: "10px 0 16px", fontFamily: "'Playfair Display',Georgia,serif", lineHeight: 1.15 }}>{content.title}</h1>
      <p style={{ color: t.text2, fontSize: 17, lineHeight: 1.75, margin: "0 0 48px", maxWidth: 680 }}>{content.intro}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
        {content.sections.map(([heading, body]) => (
          <section key={heading}>
            <h2 style={{ color: t.text, fontSize: 17, margin: "0 0 8px" }}>{heading}</h2>
            <p style={{ color: t.text2, fontSize: 14, lineHeight: 1.8, margin: 0 }}>{body}</p>
          </section>
        ))}
      </div>
      {page === "about" && (
        <button onClick={() => nav("contact")} style={{ marginTop: 42, background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 13, cursor: "pointer" }}>
          Contact QuantSoc
        </button>
      )}
    </article>
  );
}